import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../components/Icon";
import ProcessSteps from "../components/ProcessSteps";
import { PROMISES } from "../content/site";
import useDocumentTitle from "../lib/useDocumentTitle";
import f from "../components/forms.module.css";
import s from "./ImportWizard.module.css";

const STEPS = ["Výběr vozidla", "Preference", "Vaše údaje"];
const NO_PREF = "Bez preference";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidPhone = (v) => /^\+\d{1,4}( \d{3}){3}$/.test(v) || /^\+\d{10,13}$/.test(v);

export default function ImportWizard({ wizardConfig, addRequest }) {
  const { brands, models, yearCount, colors, mileages, priceTiers, wizardTitle, wizardSubtitle } = wizardConfig;
  useDocumentTitle("Dovoz elektromobilu na míru");
  // The title is editable in admin; if it ends with "na míru", that part gets the red accent.
  const accentAt = wizardTitle.toLowerCase().lastIndexOf("na míru");
  const titleLead = accentAt > 0 ? wizardTitle.slice(0, accentAt) : wizardTitle;
  const titleAccent = accentAt > 0 ? wizardTitle.slice(accentAt) : "";
  const YEARS = Array.from({ length: yearCount }, (_, i) => new Date().getFullYear() - i);

  // Home-page links can hand over a brand or ask to start right away; then skip the intro.
  const handed = useLocation().state || {};
  const presetBrand = brands.includes(handed.brand) ? handed.brand : "";
  const [step, setStep] = useState(presetBrand || handed.start ? 1 : 0); // 0 = intro, 1–3 = form steps
  const [form, setForm] = useState(() => ({
    brand: presetBrand, model: "", years: [],
    color: colors.find((x) => x === NO_PREF) || colors[colors.length - 1] || "",
    mileage: mileages.find((x) => x === NO_PREF) || mileages[mileages.length - 1] || "",
    priceRange: (priceTiers.find((t) => t.label === "Budget") || priceTiers[0] || {}).range || "",
    name: "", email: "", gsm: "+420", note: "", agreed: false,
  }));
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const topRef = useRef(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const goTo = (n) => {
    setStep(n);
    topRef.current?.scrollIntoView({ block: "start" });
  };

  // Up to three years; the last selected year can't be removed by toggling.
  function toggleYear(y) {
    setForm((p) => {
      if (p.years.includes(y)) return p.years.length === 1 ? p : { ...p, years: p.years.filter((x) => x !== y) };
      return p.years.length >= 3 ? p : { ...p, years: [...p.years, y].sort((a, b) => b - a) };
    });
  }

  const emailOk = EMAIL_RE.test(form.email);
  const phoneOk = isValidPhone(form.gsm);
  const canNext = step === 1 ? form.brand && form.model && form.years.length > 0
    : step === 2 ? form.color && form.mileage && form.priceRange
    : form.name.trim() && emailOk && phoneOk && form.agreed;

  async function submit() {
    setSending(true);
    setError("");
    try {
      await addRequest({ ...form, source: "wizard", id: Date.now(), submittedAt: new Date().toISOString(), status: "New", adminNote: "" });
      setSent(true);
      topRef.current?.scrollIntoView({ block: "start" });
    } catch (_) {
      setError("Poptávku se nepodařilo odeslat. Zkuste to prosím znovu, nebo nám zavolejte.");
    } finally {
      setSending(false);
    }
  }

  const summary = [
    ["Vozidlo", `${form.brand} ${form.model}`],
    ["Roky výroby", form.years.join(", ")],
    ["Barva", form.color],
    ["Nájezd", form.mileage],
    ["Rozpočet", form.priceRange],
  ];

  if (sent) {
    return (
      <div className="container section" ref={topRef}>
        <div className={f.success}>
          <Icon name="check-circle" size={56} strokeWidth={1.5} />
          <h1 className="h2">Poptávka odeslána</h1>
          <p className="muted">Děkujeme, {form.name.trim().split(" ")[0]}. Ozveme se vám do 24 hodin s nabídkou vozů a orientační cenou.</p>
          <dl className={f.summary}>
            {[["Jméno", form.name], ...summary, ...(form.note.trim() ? [["Poznámka", form.note.trim()]] : [])].map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
          <Link to="/nabidka" className="btn btn-primary">Prohlédnout skladové vozy</Link>
        </div>
      </div>
    );
  }

  return (
    <div ref={topRef} className={s.page}>
      <header className={s.banner}>
        <img src="/import-hero.webp" alt="" aria-hidden="true" className={s.bannerImg} />
        <div className={`container ${s.head} ${step === 0 ? s.headWithCta : ""}`}>
          <div>
            <p className="eyebrow">Dovoz z EU na míru</p>
            <h1 className={s.title}>{titleLead}{titleAccent && <span>{titleAccent}</span>}</h1>
            <p className="lead">{wizardSubtitle}</p>
          </div>
          {step === 0 && (
            <div className={`glow ${s.ctaBox}`}>
              <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => goTo(1)}>
                Vyplnit poptávku <Icon name="arrow-right" size={18} />
              </button>
              <p className={s.note}>Zabere to asi 2 minuty. Poptávka je nezávazná do chvíle, než potvrdíte konkrétní vůz.</p>
            </div>
          )}
        </div>
      </header>

      {step === 0 ? (
        <div className="container">
          <ProcessSteps onSelect={() => goTo(1)} />
          <ul className={s.promises}>
            {PROMISES.map((p) => <li key={p.text}><Icon name={p.icon} size={18} />{p.text}</li>)}
          </ul>
        </div>
      ) : (
        <div className={`container ${s.formWrap}`}>
          <ol className={s.stepper} aria-label="Kroky poptávky">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const state = n < step ? "done" : n === step ? "current" : "todo";
              return (
                <li key={label} className={s[state]} aria-current={state === "current" ? "step" : undefined}>
                  <span className={s.dot}>{state === "done" ? <Icon name="check" size={14} strokeWidth={2.5} /> : n}</span>
                  <span className={s.stepLabel}>{label}</span>
                </li>
              );
            })}
          </ol>

          <div className={f.card}>
            {step === 1 && (
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Jaký vůz hledáte?</legend>
                <div className={s.group}>
                  <span className={f.groupLabel}>Značka</span>
                  <div className={f.chips}>
                    {brands.map((b) => (
                      <button key={b} type="button" className={f.chip} aria-pressed={form.brand === b}
                        onClick={() => setForm((p) => ({ ...p, brand: b, model: "", years: [] }))}>{b}</button>
                    ))}
                  </div>
                </div>
                {form.brand && (
                  <div className={s.group}>
                    <span className={f.groupLabel}>Model</span>
                    <div className={f.chips}>
                      {(models[form.brand] || []).map((m) => (
                        <button key={m} type="button" className={f.chip} aria-pressed={form.model === m}
                          onClick={() => setForm((p) => ({ ...p, model: m, years: [] }))}>{m}</button>
                      ))}
                    </div>
                  </div>
                )}
                {form.model && (
                  <div className={s.group}>
                    <span className={f.groupLabel}>Rok výroby <span className={s.hint}>— vyberte 1 až 3 roky ({form.years.length}/3)</span></span>
                    <div className={f.chips}>
                      {YEARS.map((y) => {
                        const selected = form.years.includes(y);
                        return (
                          <button key={y} type="button" className={`${f.chip} num`} aria-pressed={selected}
                            disabled={!selected && form.years.length >= 3} onClick={() => toggleYear(y)}>
                            {selected && <Icon name="check" size={16} strokeWidth={2.25} />}{y}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </fieldset>
            )}

            {step === 2 && (
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Upřesněte parametry</legend>
                <div className={s.group}>
                  <span className={f.groupLabel}>Preferovaná barva</span>
                  <div className={f.chips}>
                    {colors.map((c) => (
                      <button key={c} type="button" className={f.chip} aria-pressed={form.color === c} onClick={() => set("color", c)}>{c}</button>
                    ))}
                  </div>
                </div>
                <div className={s.group}>
                  <span className={f.groupLabel}>Maximální nájezd</span>
                  <div className={f.chips}>
                    {mileages.map((m) => (
                      <button key={m} type="button" className={f.chip} aria-pressed={form.mileage === m} onClick={() => set("mileage", m)}>{m}</button>
                    ))}
                  </div>
                </div>
                <div className={s.group}>
                  <span className={f.groupLabel}>Rozpočet</span>
                  <div className={s.tiers}>
                    {priceTiers.map((tier) => (
                      <button key={tier.label} type="button" className={s.tier} aria-pressed={form.priceRange === tier.range}
                        onClick={() => set("priceRange", tier.range)}>
                        <span className={s.tierRange}>{tier.range}</span>
                        <span className={s.tierDesc}>{tier.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </fieldset>
            )}

            {step === 3 && (
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Kam vám pošleme nabídku?</legend>
                <dl className={`${f.summary} ${s.summary}`}>
                  {summary.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
                </dl>
                <label className={f.field}>
                  <span className={f.label}>Jméno a příjmení</span>
                  <input type="text" name="name" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
                </label>
                <div className={f.row}>
                  <label className={f.field}>
                    <span className={f.label}>E-mail</span>
                    <input type="email" name="email" autoComplete="email" inputMode="email" value={form.email}
                      onChange={(e) => set("email", e.target.value)} onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                      aria-invalid={touched.email && !emailOk} aria-describedby="w-email-err" required />
                    {touched.email && !emailOk && <span id="w-email-err" className={f.error}><Icon name="alert-circle" size={16} />Zadejte platný e-mail, např. jana@example.com</span>}
                  </label>
                  <label className={f.field}>
                    <span className={f.label}>Telefon</span>
                    <input type="tel" name="tel" autoComplete="tel" inputMode="tel" value={form.gsm}
                      onChange={(e) => set("gsm", e.target.value)} onBlur={() => setTouched((t) => ({ ...t, gsm: true }))}
                      aria-invalid={touched.gsm && !phoneOk} aria-describedby="w-tel-err" required />
                    {touched.gsm && !phoneOk && <span id="w-tel-err" className={f.error}><Icon name="alert-circle" size={16} />Formát +420 123 456 789</span>}
                  </label>
                </div>
                <label className={f.field}>
                  <span className={f.label}>Poznámka<span className={f.optional}>nepovinné</span></span>
                  <textarea rows={4} value={form.note} onChange={(e) => set("note", e.target.value)}
                    placeholder="Např. tmavě modrá barva, maximálně 2 majitelé, tažné zařízení…" />
                </label>
                <label className={f.check}>
                  <input type="checkbox" checked={form.agreed} onChange={(e) => set("agreed", e.target.checked)} />
                  <span>Souhlasím s podáním poptávky na výše popsané vozidlo. VoltNaPřání mě do 24 hodin kontaktuje e-mailem nebo telefonicky s nabídkou vozů a jejich přibližnou cenou.</span>
                </label>
              </fieldset>
            )}

            {error && <div className={`${f.alert} ${f.alertError}`} role="alert" style={{ marginTop: 20 }}><Icon name="alert-circle" size={18} />{error}</div>}

            <div className={s.nav}>
              <button type="button" className="btn btn-ghost" onClick={() => goTo(step - 1)}>
                <Icon name="arrow-left" size={18} /> Zpět
              </button>
              {step < 3 ? (
                <button type="button" className="btn btn-primary" disabled={!canNext} onClick={() => goTo(step + 1)}>
                  Pokračovat <Icon name="arrow-right" size={18} />
                </button>
              ) : (
                <button type="button" className="btn btn-primary" disabled={!canNext || sending} onClick={submit}>
                  {sending ? "Odesílám…" : "Odeslat poptávku"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
